<template>
  <v-container class="mt-5">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-card elevation="3">
          <v-card-title class="text-h5">
            Actualizar Información de Usuario
          </v-card-title>
          <v-card-text>
            <v-form ref="form">
              <!-- Campo para nombre -->
              <v-text-field
                v-model="nombre"
                label="Nuevo Nombre"
                outlined
                dense
                required
                placeholder="Introduce tu nuevo nombre"
              ></v-text-field>
              <!-- Sección para foto de perfil -->
              <v-row class="mt-4" align="center">
                <!-- Vista previa del avatar -->
                <v-col cols="12" md="4" class="text-center">
                  <v-avatar size="120">
                    <v-img :src="previewImage" alt="Foto de perfil" />
                  </v-avatar>
                </v-col>
                <!-- Input para seleccionar la imagen -->
                <v-col cols="12" md="8">
                  <v-file-input
                    v-model="selectedFile"
                    label="Selecciona una nueva foto de perfil"
                    accept="image/*"
                    outlined
                    dense
                  ></v-file-input>
                </v-col>
              </v-row>
              <v-divider class="my-4"></v-divider>
              <v-card-subtitle class="text-h6"
                >Cambiar Contraseña</v-card-subtitle
              >

              <v-text-field
                v-model="currentPassword"
                label="Contraseña Actual"
                type="password"
                outlined
                dense
                placeholder="Introduce tu contraseña actual"
                class="mt-2"
              ></v-text-field>

              <v-text-field
                v-model="newPassword"
                label="Nueva Contraseña"
                type="password"
                outlined
                dense
                placeholder="Introduce una nueva contraseña"
                class="mt-2"
              ></v-text-field>

              <v-text-field
                v-model="confirmPassword"
                label="Confirmar Nueva Contraseña"
                type="password"
                outlined
                dense
                placeholder="Repite la nueva contraseña"
                class="mt-2"
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="actualizar" :disabled="!nombre">
              <v-icon left>mdi-check</v-icon>Actualizar
            </v-btn>
          </v-card-actions>
        </v-card>
        <!-- Mensaje informativo -->
        <v-alert
          v-if="mensaje"
          :type="mensajeExito ? 'success' : 'error'"
          class="mt-4"
          dismissible
        >
          {{ mensaje }}<br />
          *Para ver la imagen actualizada, cierre sesión y vuelva a iniciar
          sesión.
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/services/api";
import { useAuthStore } from "@/stores/authStore";

/**
 * Función auxiliar para comprimir y redimensionar la imagen usando un canvas.
 * @param {File} file - Archivo de imagen original.
 * @param {number} quality - Calidad (0 a 1).
 * @param {number} maxWidth - Ancho máximo.
 * @param {number} maxHeight - Alto máximo.
 * @returns {Promise<Blob>} Promise que resuelve en un Blob comprimido.
 */
function compressImage(file, quality = 0.7, maxWidth = 800, maxHeight = 800) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      img.src = event.target.result;
    };
    reader.onerror = (error) => reject(error);
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Error comprimiendo la imagen."));
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = (error) => reject(error);
  });
}

export default {
  name: "ProfileUpdate",
  data() {
    return {
      nombre: "",
      // Se carga la imagen actual del authStore
      fotoPerfil: "",
      mensaje: null,
      mensajeExito: false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      // Placeholder en gris
      defaultPhoto:
        "https://dummyimage.com/150x150/cccccc/ffffff.png&text=No+Image",
      selectedFile: null,
    };
  },
  computed: {
    /**
     * Devuelve la imagen a mostrar:
     * - Si se selecciona un nuevo archivo, se crea una URL temporal.
     * - Si no, se muestra la imagen actual (fotoPerfil) si existe, o el placeholder.
     */
    previewImage() {
      if (this.selectedFile) {
        return URL.createObjectURL(this.selectedFile);
      } else if (this.fotoPerfil && this.fotoPerfil.trim() !== "") {
        return this.fotoPerfil;
      } else {
        return this.defaultPhoto;
      }
    },
  },
  mounted() {
    const authStore = useAuthStore();
    if (authStore.user) {
      this.nombre = authStore.user.name || "";
      this.fotoPerfil = authStore.user.photo || "";
    }
  },
  methods: {
    async actualizar() {
      try {
        let newPhotoUrl = this.fotoPerfil;

        // 1. Subir imagen si hay nueva seleccionada
        if (this.selectedFile) {
          const compressedBlob = await compressImage(
            this.selectedFile,
            0.7,
            800,
            800
          );
          const formData = new FormData();
          formData.append("file", compressedBlob, this.selectedFile.name);
          const uploadResponse = await api.uploadProfileImage(formData);
          newPhotoUrl = uploadResponse.url;
          this.fotoPerfil = newPhotoUrl;
          this.selectedFile = null;
        }

        // 2. Verificar y cambiar contraseña si se ha introducido
        if (this.currentPassword || this.newPassword || this.confirmPassword) {
          if (this.newPassword !== this.confirmPassword) {
            this.mensaje = "Las contraseñas nuevas no coinciden.";
            this.mensajeExito = false;
            return;
          }

          try {
            await api.cambiarContrasena({
              current_password: this.currentPassword,
              new_password: this.newPassword,
            });
          } catch (error) {
            this.mensaje = "Error al cambiar la contraseña.";
            this.mensajeExito = false;
            return;
          }
        }

        // 3. Llamada para actualizar nombre y/o foto
        const response = await api.actualizarUsuario({
          nombre: this.nombre,
          foto_perfil: newPhotoUrl || undefined,
        });

        this.mensaje = response.message;
        this.mensajeExito = true;

        const authStore = useAuthStore();
        authStore.updateUser({
          name: response.user.username,
          photo: response.user.foto_perfil,
        });
      } catch (error) {
        console.error("Error en la actualización:", error);
        this.mensaje = "Hubo un problema al actualizar los datos.";
        this.mensajeExito = false;
      }
    },
  },
};
</script>

<style scoped>
.v-container {
  max-width: 1200px;
}
</style>
